import React from 'react';
import { Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Line, ComposedChart } from 'recharts';

const RevenueBreakdownChart = ({ data, period }) => {
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-4 shadow-lg">
          <p className="text-sm font-semibold text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4 mb-1">
              <span className="caption text-muted-foreground">{entry?.name}:</span>
              <span className="text-sm font-semibold text-foreground font-data">
                {entry?.name === 'Margin' ? `${entry?.value}%` : `$${entry?.value?.toLocaleString()}`}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="card">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground mb-1">
            Revenue Breakdown by Source
          </h3>
          <p className="caption text-muted-foreground">
            Fee income with profit margin overlay - {period}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-primary"></div>
            <span className="caption text-muted-foreground">Swap Fees</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-secondary"></div>
            <span className="caption text-muted-foreground">Merchant Fees</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-accent"></div>
            <span className="caption text-muted-foreground">Subscriptions</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-success"></div>
            <span className="caption text-muted-foreground">Margin %</span>
          </div>
        </div>
      </div>

      <div className="w-full h-80 md:h-96 lg:h-[400px]" aria-label="Revenue breakdown composed chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="month" 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px', fontFamily: 'Source Sans 3' }}
            />
            <YAxis 
              yAxisId="left"
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px', fontFamily: 'JetBrains Mono' }}
              tickFormatter={(value) => `$${value}k`}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              stroke="var(--color-success)"
              style={{ fontSize: '12px', fontFamily: 'JetBrains Mono' }}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '20px', fontFamily: 'Source Sans 3', fontSize: '12px' }}
            />
            <Bar yAxisId="left" dataKey="swapFees" name="Swap Fees" fill="var(--color-primary)" radius={[8, 8, 0, 0]} />
            <Bar yAxisId="left" dataKey="merchantFees" name="Merchant Fees" fill="var(--color-secondary)" radius={[8, 8, 0, 0]} />
            <Bar yAxisId="left" dataKey="subscriptions" name="Subscriptions" fill="var(--color-accent)" radius={[8, 8, 0, 0]} />
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="margin" 
              name="Margin" 
              stroke="var(--color-success)" 
              strokeWidth={3}
              dot={{ fill: 'var(--color-success)', r: 4 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RevenueBreakdownChart;